
import { supabase } from '@/integrations/supabase/client';
import { currentEnvironment, config } from '@/config/environment';
import { CronJobManager } from '@/utils/cron/cronJobManager';
import { EnvironmentEmailConfigManager } from '@/utils/email/environmentEmailConfig';

export class EnvironmentInitializer {
  /**
   * Initialize the current environment with proper configuration
   */
  static async initializeEnvironment(): Promise<{ success: boolean; error?: string; details?: any }> {
    try {
      console.log(`Initializing environment: ${currentEnvironment}`);
      
      const results = {
        emailConfig: false,
        cronJobs: false,
        cleanup: false,
        jobsCreated: 0,
        errors: [] as string[]
      };

      // 1. Initialize email configuration
      try {
        const emailResult = await EnvironmentEmailConfigManager.initializeEmailConfig();
        results.emailConfig = emailResult.success;
        if (!emailResult.success) {
          console.warn('Email config initialization failed:', emailResult.error);
          results.errors.push(`Email config: ${emailResult.error}`);
        } else {
          console.log('Email configuration initialized successfully');
        }
      } catch (error) {
        console.error('Error initializing email config:', error);
        results.errors.push(`Email config exception: ${error instanceof Error ? error.message : 'Unknown'}`);
      }

      // 2. Clean up cross-environment cron jobs
      try {
        const cleanupResult = await CronJobManager.cleanupCrossEnvironmentJobs();
        results.cleanup = cleanupResult.success;
        console.log(`Cleaned up ${cleanupResult.cleaned} cross-environment cron jobs`);
      } catch (error) {
        console.error('Error cleaning up cron jobs:', error);
        results.errors.push(`Cleanup error: ${error instanceof Error ? error.message : 'Unknown'}`);
      }

      // 3. Initialize environment-specific cron jobs
      try {
        const jobsResult = await CronJobManager.initializeEnvironmentJobs();
        results.cronJobs = jobsResult.success;
        results.jobsCreated = jobsResult.created;
        
        if (jobsResult.errors.length > 0) {
          results.errors.push(...jobsResult.errors);
        }
        
        console.log(`Created ${jobsResult.created} cron jobs for ${currentEnvironment}`);
      } catch (error) {
        console.error('Error creating cron jobs:', error);
        results.errors.push(`Cron jobs error: ${error instanceof Error ? error.message : 'Unknown'}`);
      }

      // 4. Store environment initialization status
      try {
        await supabase
          .from('app_settings')
          .upsert({
            key: `environment_initialized_${currentEnvironment}`,
            value: {
              initialized_at: new Date().toISOString(),
              environment: currentEnvironment,
              api_url: config.apiUrl,
              results: results
            }
          }, { onConflict: 'key' });
      } catch (error) {
        console.error('Error storing initialization status:', error);
        results.errors.push(`Status storage error: ${error instanceof Error ? error.message : 'Unknown'}`);
      }

      const allSuccessful = results.emailConfig && results.cronJobs;
      
      return {
        success: allSuccessful,
        details: results,
        error: allSuccessful ? undefined : `Some initialization steps failed: ${results.errors.join(', ')}`
      };

    } catch (error) {
      console.error('Environment initialization failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        details: null
      };
    }
  }

  /**
   * Check if environment is properly initialized
   */
  static async checkEnvironmentStatus(): Promise<{
    initialized: boolean;
    lastInitialized?: string;
    details?: any;
  }> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', `environment_initialized_${currentEnvironment}`)
        .maybeSingle();

      if (error || !data) {
        return { initialized: false };
      }

      const initData = data.value as any;
      return {
        initialized: true,
        lastInitialized: initData.initialized_at,
        details: initData.results
      };
    } catch (error) {
      console.error('Error checking environment status:', error);
      return { initialized: false };
    }
  }
}

// Auto-initialize on import in development environment only
if (typeof window !== 'undefined' && currentEnvironment === 'development') {
  EnvironmentInitializer.checkEnvironmentStatus().then(status => {
    if (!status.initialized) {
      console.log('Development environment not initialized, running auto-initialization...');
      EnvironmentInitializer.initializeEnvironment().then(result => {
        if (result.success) {
          console.log('Development environment initialized successfully');
        } else {
          console.warn('Development environment initialization had issues:', result.error);
        }
      });
    }
  });
}
